import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Pencil, Plus, Power, Trash2, TriangleAlert, X } from 'lucide-react';
import { clsx } from 'clsx';

import type { RegrasDeAjuste } from '@engine/ajustes.ts';
import type { RegraDeProjecao, VisaoDoRelatorio, VisaoPorPilar } from '@engine/tipos.ts';

import { api } from '../lib/api.ts';
import { useAuth } from '../contexto/auth.tsx';
import {
  CARGOS,
  competencia,
  data as formatarData,
  primeiroDiaDaCompetenciaAtual,
  reais,
} from '../lib/formato.ts';
import {
  Aviso,
  Botao,
  Campo,
  Carregando,
  Cartao,
  Etiqueta,
  Selecao,
  Tabela,
} from '../componentes/ui.tsx';

/* ------------------------------------------------------------------ tipos */

interface VersaoResumo {
  id: string;
  nome: string;
  status: string;
  vigencia_inicio: string;
  vigencia_fim: string | null;
  observacao: string | null;
  criado_por: string | null;
  faixas: string;
  fechamentos: string;
}

interface Faixa {
  id: string;
  metrica: string;
  cargo_codigo: string;
  fase: string;
  ordem: number;
  minimo: string;
  valor: string;
}

interface FaixaBalizador {
  id: string;
  cargo_codigo: string;
  rotulo: string;
  ordem: number;
  limite_superior: string | null;
  fator: string;
}

interface Filtro {
  id: string;
  base: string;
  tipo: string;
  operador: string;
  valor: string;
  rotulo: string | null;
  papel: string;
  observacao: string | null;
}

interface Parametro {
  chave: string;
  valor: unknown;
  descricao: string | null;
}

interface Detalhe {
  versao: VersaoResumo & { fechamentos: string };
  faixas: Faixa[];
  balizador: FaixaBalizador[];
  filtros: Filtro[];
  parametros: Parametro[];
  problemas: string[];
}

/* ----------------------------------------------------------- utilitários */

/**
 * O banco guarda fração (0,72) e a tela mostra percentual (72). A conversão
 * mora só aqui e no ponto de envio — misturar as duas unidades no meio do
 * caminho é a forma mais fácil de trocar 72% por 7200%.
 */
const paraPercentual = (fracao: string | number | null): string =>
  fracao === null ? '' : String(Number(fracao) * 100).replace('.', ',');

const paraFracao = (percentual: string): number =>
  Number(percentual.replace(',', '.')) / 100;

const numeroBr = (v: string): number => Number(v.replace(',', '.'));

/* -------------------------------------------------------------- página */

export function Regras() {
  const cache = useQueryClient();
  const { pode } = useAuth();
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);

  const versoes = useQuery({
    queryKey: ['regras', 'versoes'],
    queryFn: () =>
      api<{ versoes: VersaoResumo[]; vigenteHojeId: string | null }>('/regras/versoes'),
  });

  const detalhe = useQuery({
    queryKey: ['regras', 'versao', selecionada],
    queryFn: () => api<Detalhe>(`/regras/versoes/${selecionada}`),
    enabled: selecionada !== null,
  });

  // Abre a vigente por padrão: é a que interessa em 9 de 10 visitas.
  useEffect(() => {
    if (selecionada === null && versoes.data !== undefined) {
      const ativa = versoes.data.versoes.find((v) => v.status === 'ativa');
      setSelecionada(ativa?.id ?? versoes.data.versoes[0]?.id ?? null);
    }
  }, [versoes.data, selecionada]);

  const recarregar = () => {
    void cache.invalidateQueries({ queryKey: ['regras'] });
  };

  if (versoes.isLoading) return <Carregando />;
  if (versoes.isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível carregar as regras">
        <p>{versoes.error.message}</p>
      </Aviso>
    );
  }

  // isLoading não estreita o tipo de data para o TypeScript; o guarda explícito
  // faz isso e cobre o caso de erro silencioso.
  if (versoes.data === undefined) return null;

  return (
    <div className="space-y-6">
      <Aviso tom="info" titulo="Como funciona a vigência">
        <p>
          Uma versão <strong>ativa não se edita</strong>. Para mudar uma faixa, duplique a vigente,
          ajuste o rascunho, diga a partir de quando vale e ative.
        </p>
        <p>
          É isso que impede uma alteração de hoje de mudar em silêncio a prévia que alguém está
          conferindo — e fechamento já gravado nunca é afetado, porque ele guarda a própria cópia
          das regras.
        </p>
      </Aviso>

      {versoes.data !== undefined && versoes.data.vigenteHojeId === null && (
        <Aviso tom="erro" titulo="Nenhuma versão está vigente hoje">
          <p>
            O fechamento vai recusar enquanto estiver assim — ele procura a versão em vigor na
            data final do período apurado e não encontra nenhuma.
          </p>
          <p className="mt-2">
            Acontece quando a versão que valia foi excluída, ou quando todas foram encerradas sem
            que outra assumisse. Para resolver: abra a versão que deveria valer e, em{' '}
            <strong>Editar vigência</strong>, ajuste a data de início; ou ative uma versão nova.
          </p>
        </Aviso>
      )}

      <Cartao
        titulo="Versões de regras"
        acoes={
          pode('regras', 'editar') && (
            <Botao onClick={() => setCriando(true)}>
              <Plus className="size-4" />
              Nova versão
            </Botao>
          )
        }
      >
        <Tabela
          cabecalho={
            <tr>
              <th className="py-2 pr-3">Nome</th>
              <th className="py-2 pr-3">Vigência</th>
              <th className="py-2 pr-3">Situação</th>
              <th className="py-2 pr-3 text-right">Faixas</th>
              <th className="py-2 pr-3 text-right">Fechamentos</th>
              <th className="py-2 pr-3">Criada por</th>
            </tr>
          }
        >
          {versoes.data.versoes.map((v) => (
            <tr
              key={v.id}
              onClick={() => setSelecionada(v.id)}
              className={
                v.id === selecionada
                  ? 'cursor-pointer bg-slate-100'
                  : 'cursor-pointer hover:bg-slate-50'
              }
            >
              <td className="py-2 pr-3 font-medium">{v.nome}</td>
              <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                {competencia(v.vigencia_inicio)}
                {v.vigencia_fim === null
                  ? ' — em diante'
                  : ` — ${competencia(v.vigencia_fim)}`}
              </td>
              <td className="py-2 pr-3">
                <Etiqueta
                  tom={v.status === 'ativa' ? 'bom' : v.status === 'rascunho' ? 'medio' : 'neutro'}
                >
                  {v.status}
                </Etiqueta>
              </td>
              <td className="num py-2 pr-3 text-slate-500">{v.faixas}</td>
              <td className="num py-2 pr-3 text-slate-500">{v.fechamentos}</td>
              <td className="py-2 pr-3 text-slate-500">{v.criado_por ?? '—'}</td>
            </tr>
          ))}
        </Tabela>
      </Cartao>

      {criando && (
        <NovaVersao
          versoes={versoes.data.versoes}
          onFechar={() => setCriando(false)}
          onCriada={(id) => {
            setCriando(false);
            setSelecionada(id);
            recarregar();
          }}
        />
      )}

      {detalhe.isLoading && <Carregando />}
      {detalhe.data !== undefined && (
        <DetalheVersao dados={detalhe.data} onMudou={recarregar} onExcluida={() => setSelecionada(null)} />
      )}
    </div>
  );
}

/* --------------------------------------------------------- nova versão */

function NovaVersao({
  versoes,
  onFechar,
  onCriada,
}: {
  versoes: VersaoResumo[];
  onFechar: () => void;
  onCriada: (id: string) => void;
}) {
  const ativa = versoes.find((v) => v.status === 'ativa');
  const [nome, setNome] = useState('');
  // A competência que se está apurando, não hoje: o fechamento sempre roda
  // sobre o mês passado, então era essa a data que todo mundo tinha de
  // corrigir à mão toda vez.
  const [vigencia, setVigencia] = useState(primeiroDiaDaCompetenciaAtual());
  const [copiarDe, setCopiarDe] = useState(ativa?.id ?? '');
  const [observacao, setObservacao] = useState('');

  const criar = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/regras/versoes', {
        metodo: 'POST',
        corpo: { nome, vigenciaInicio: vigencia, copiarDe: copiarDe === '' ? null : copiarDe, observacao },
      }),
    onSuccess: (r) => onCriada(r.id),
  });

  return (
    <Cartao titulo="Nova versão de regras">
      <div className="max-w-2xl space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Nome"
            valor={nome}
            onChange={setNome}
            dica='Algo que diga o que mudou. Ex.: "Acordo 2026 rev. setembro".'
          />
          <Campo
            rotulo="Vale a partir de"
            tipo="date"
            valor={vigencia}
            onChange={setVigencia}
            dica={
              vigencia === ''
                ? 'A versão anterior é encerrada no dia anterior a esta data.'
                : `Vale da competência de ${competencia(vigencia)} em diante. A versão anterior é encerrada no dia anterior.`
            }
          />
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-medium tracking-wide text-slate-600 uppercase">
            Copiar regras de
          </span>
          <select
            value={copiarDe}
            onChange={(e) => setCopiarDe(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">Começar vazia (vou cadastrar tudo)</option>
            {versoes.map((v) => (
              <option key={v.id} value={v.id}>
                {v.nome} ({v.status})
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-slate-500">
            Duplicar é o caminho normal — ninguém quer cadastrar 60 faixas de novo para mudar uma.
          </span>
        </label>

        <Campo rotulo="Observação" valor={observacao} onChange={setObservacao} />

        {criar.isError && (
          <Aviso tom="erro">
            <p>{criar.error.message}</p>
          </Aviso>
        )}

        <div className="flex gap-3">
          <Botao
            onClick={() => criar.mutate()}
            carregando={criar.isPending}
            desabilitado={nome.trim() === '' || vigencia === ''}
          >
            Criar rascunho
          </Botao>
          <Botao tipo="secundario" onClick={onFechar}>
            Cancelar
          </Botao>
        </div>
      </div>
    </Cartao>
  );
}

/* ------------------------------------------------------ detalhe/edição */

function DetalheVersao({
  dados,
  onMudou,
  onExcluida,
}: {
  dados: Detalhe;
  onMudou: () => void;
  onExcluida: () => void;
}) {
  const v = dados.versao;
  // Duas condições diferentes: a versão precisa ser rascunho E a pessoa
  // precisa ter edição em Regras. Antes só a primeira era conferida na tela,
  // e quem não podia editar só descobria ao salvar e tomar 403.
  const { pode } = useAuth();
  const rascunho = v.status === 'rascunho' && pode('regras', 'editar');

  // Ativar nao e a mesma coisa que editar. O conteudo das regras so muda em
  // rascunho; voltar a valer e outra acao, e uma versao arquivada por engano
  // precisa ter esse caminho — senao ela fica na lista sem servir para nada.
  const podeAtivar = v.status !== 'ativa' && pode('regras', 'editar');
  const usos = Number(v.fechamentos);
  const temFechamentos = usos > 0;

  // "Vigente hoje" é diferente de "ativa": uma versão ativa com vigência
  // futura ainda pode ser excluída sem deixar o fechamento sem regra.
  const hoje = new Date().toISOString().slice(0, 10);
  const vigenteHoje =
    v.status === 'ativa' &&
    v.vigencia_inicio <= hoje &&
    (v.vigencia_fim === null || v.vigencia_fim >= hoje);

  // Só o uso em fechamento impede excluir. Ser a vigente de hoje passou a ser
  // um aviso, não um bloqueio: uma versão ativada por engano com a vigência
  // errada não tinha nenhuma saída pela tela.
  const podeExcluir = !temFechamentos && pode('regras', 'excluir');
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [editandoCabecalho, setEditandoCabecalho] = useState(false);

  const cargos = useMemo(
    () => [...new Set(dados.faixas.map((f) => f.cargo_codigo))],
    [dados.faixas],
  );

  const [metrica, setMetrica] = useState('comparecimento');
  const [cargo, setCargo] = useState(cargos[0] ?? 'atendente');

  const duplicar = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/regras/versoes', {
        metodo: 'POST',
        corpo: {
          nome: `${v.nome} (cópia)`,
          // Antes vinha a data de hoje, que nunca é a certa: apura-se o mês
          // passado, então o rascunho nasce valendo da competência apurada.
          vigenciaInicio: primeiroDiaDaCompetenciaAtual(),
          copiarDe: v.id,
        },
      }),
    onSuccess: onMudou,
  });

  const [supersedidaPor, setSupersedidaPor] = useState<{
    nome: string;
    vigencia_inicio: string;
  } | null>(null);

  const ativar = useMutation({
    mutationFn: () =>
      api<{ ok: true; supersedidaPor: { nome: string; vigencia_inicio: string } | null }>(
        `/regras/versoes/${v.id}/ativar`,
        { metodo: 'POST' },
      ),
    onSuccess: (r) => {
      setSupersedidaPor(r.supersedidaPor);
      onMudou();
    },
  });

  const excluir = useMutation({
    mutationFn: () => api<{ ok: true }>(`/regras/versoes/${v.id}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      onExcluida();
      onMudou();
    },
  });

  return (
    <div className="space-y-6">
      <Cartao
        titulo={v.nome}
        descricao={`${v.status} · vale de ${formatarData(v.vigencia_inicio)}${v.vigencia_fim === null ? ' em diante' : ` a ${formatarData(v.vigencia_fim)}`} · competência de ${competencia(v.vigencia_inicio)}`}
        acoes={
          <div className="flex flex-wrap gap-2">
            {pode('regras', 'editar') && (
            <Botao tipo="secundario" onClick={() => duplicar.mutate()} carregando={duplicar.isPending}>
              <Copy className="size-4" />
              Duplicar para editar
            </Botao>
            )}
            {pode('regras', 'editar') && !temFechamentos && (
              <Botao tipo="secundario" onClick={() => setEditandoCabecalho((e) => !e)}>
                <Pencil className="size-4" />
                Editar vigência
              </Botao>
            )}
            {podeAtivar && (
              <Botao
                onClick={() => ativar.mutate()}
                carregando={ativar.isPending}
                desabilitado={dados.problemas.length > 0}
              >
                <Power className="size-4" />
                {v.status === 'arquivada' ? 'Voltar a valer' : 'Ativar'}
              </Botao>
            )}
            {podeExcluir && (
              <Botao tipo="perigo" onClick={() => setConfirmandoExclusao(true)}>
                <Trash2 className="size-4" />
                Excluir
              </Botao>
            )}
          </div>
        }
      >
        <div className="space-y-4">
          {!rascunho && (
            <Aviso tom="atencao" titulo="Esta versão não pode ser editada">
              <p>
                Ela {v.status === 'ativa' ? 'está valendo' : 'já valeu'}
                {temFechamentos ? ` e foi usada em ${v.fechamentos} fechamento(s)` : ''}. Use
                "Duplicar para editar" para mudar faixas e parâmetros — assim o histórico continua
                explicando cada valor pago. As datas de vigência dá para corrigir aqui mesmo,
                enquanto a versão não tiver nenhum fechamento.
              </p>
            </Aviso>
          )}

          {dados.problemas.length > 0 && (
            <Aviso tom="erro" titulo={`${dados.problemas.length} problema(s) na configuração`}>
              {dados.problemas.map((p) => (
                <p key={p}>· {p}</p>
              ))}
              <p className="mt-1 font-medium">
                Enquanto houver problema aqui, esta versão não pode ser ativada.
              </p>
            </Aviso>
          )}

          {ativar.isError && (
            <Aviso tom="erro">
              <p>{ativar.error.message}</p>
            </Aviso>
          )}

          {editandoCabecalho && (
            <EditarCabecalho
              versao={v}
              aoSalvar={() => {
                setEditandoCabecalho(false);
                onMudou();
              }}
              aoCancelar={() => setEditandoCabecalho(false)}
            />
          )}

          {supersedidaPor !== null && (
            <Aviso titulo="Ativada, mas com prazo">
              <p>
                Já existe uma versão ativa começando em{' '}
                {formatarData(supersedidaPor.vigencia_inicio)} — "{supersedidaPor.nome}". A partir
                daquela data é ela que vale, porque a vigência é resolvida pela data de início mais
                recente que cobre o dia. Esta aqui vale só até lá.
              </p>
            </Aviso>
          )}

          {rascunho && dados.problemas.length === 0 && (
            <Aviso tom="info">
              <p>Configuração coerente. Pode ativar.</p>
            </Aviso>
          )}

          {temFechamentos && (
            <Aviso tom="info" titulo="Esta versão não pode ser excluída">
              <p>
                Ela foi usada em {usos} fechamento(s). Mesmo que cada fechamento guarde a própria
                cópia das regras, apagar a origem quebraria a rastreabilidade de um valor que já
                foi pago a alguém.
              </p>
            </Aviso>
          )}

          {!temFechamentos && vigenteHoje && (
            <Aviso tom="info" titulo="Esta é a versão em vigor hoje">
              <p>
                Ela nunca foi usada num fechamento, então dá para excluir. Ao confirmar, o sistema
                diz qual versão passa a valer no lugar — e avisa se nenhuma passar, caso em que o
                próximo cálculo vai recusar até alguém ativar uma.
              </p>
            </Aviso>
          )}

          {confirmandoExclusao && (
            <div className="rounded border border-red-300 bg-red-50 p-4">
              <p className="font-semibold text-red-900">Excluir "{v.nome}"?</p>
              <p className="mt-1 text-sm text-red-900">
                As faixas, os pisos e os filtros desta versão vão junto. Como ela nunca foi usada
                em nenhum fechamento, nada já calculado muda de valor.
              </p>
              {vigenteHoje && (
                <p className="mt-2 text-sm text-red-900">
                  É a versão em vigor hoje: depois de excluir, quem passa a valer é a versão ativa
                  anterior que cobrir a data. Se não houver nenhuma, o próximo fechamento vai
                  recusar com essa mensagem até você ativar uma.
                </p>
              )}

              {excluir.isError && (
                <p className="mt-2 text-sm font-medium text-red-900">{excluir.error.message}</p>
              )}

              <div className="mt-4 flex gap-3">
                <Botao
                  tipo="perigo"
                  onClick={() => excluir.mutate()}
                  carregando={excluir.isPending}
                >
                  Confirmar exclusão
                </Botao>
                <Botao tipo="secundario" onClick={() => setConfirmandoExclusao(false)}>
                  Cancelar
                </Botao>
              </div>
            </div>
          )}

          {v.observacao !== null && <p className="text-sm text-slate-600">{v.observacao}</p>}
        </div>
      </Cartao>

      <Cartao
        titulo="Faixas de premiação"
        descricao="A faixa vale do mínimo até o mínimo da próxima. A última é aberta — não existe teto, então uma taxa muito alta nunca fica sem faixa."
        acoes={
          <div className="flex gap-3">
            <Selecao
              rotulo="Métrica"
              valor={metrica}
              onChange={setMetrica}
              opcoes={['comparecimento', 'ocupacao']}
              vazio=""
            />
            <Selecao rotulo="Cargo" valor={cargo} onChange={setCargo} opcoes={cargos} vazio="" />
          </div>
        }
      >
        <EditorFaixas
          versaoId={v.id}
          metrica={metrica}
          cargo={cargo}
          faixas={dados.faixas.filter((f) => f.metrica === metrica && f.cargo_codigo === cargo)}
          editavel={rascunho}
          onSalvo={onMudou}
        />
      </Cartao>

      <Cartao
        titulo="Balizador de produção"
        descricao="Compara o volume de agendamentos de cada atendente com a média da equipe. O teto é em percentual da média; a última faixa fica sem teto."
      >
        <EditorBalizador
          versaoId={v.id}
          faixas={dados.balizador}
          editavel={rascunho}
          onSalvo={onMudou}
        />
      </Cartao>

      <Cartao titulo="Pisos e parâmetros">
        <EditorParametros
          versaoId={v.id}
          parametros={dados.parametros}
          cargos={cargos}
          editavel={rascunho}
          onSalvo={onMudou}
        />
      </Cartao>

      <Cartao
        titulo="Status que contam em cada base"
        descricao="As duas bases saem do mesmo relatório com regras diferentes. É aqui que se decide, por exemplo, que Remarcado conta no balizador mas não na taxa de comparecimento."
      >
        <EditorFiltros
          versaoId={v.id}
          filtros={dados.filtros}
          editavel={rascunho}
          onSalvo={onMudou}
        />
      </Cartao>
    </div>
  );
}

/* -------------------------------------------------------- editor faixas */

function EditorFaixas({
  versaoId,
  metrica,
  cargo,
  faixas,
  editavel,
  onSalvo,
}: {
  versaoId: string;
  metrica: string;
  cargo: string;
  faixas: Faixa[];
  editavel: boolean;
  onSalvo: () => void;
}) {
  const [linhas, setLinhas] = useState(
    faixas.map((f) => ({ fase: f.fase, minimo: paraPercentual(f.minimo), valor: f.valor })),
  );

  useEffect(() => {
    setLinhas(faixas.map((f) => ({ fase: f.fase, minimo: paraPercentual(f.minimo), valor: f.valor })));
  }, [faixas]);

  const salvar = useMutation({
    mutationFn: () =>
      api<{ ok: true }>(`/regras/versoes/${versaoId}/faixas`, {
        metodo: 'PUT',
        corpo: {
          metrica,
          cargoCodigo: cargo,
          faixas: linhas.map((l) => ({
            fase: l.fase,
            minimo: paraFracao(l.minimo),
            valor: numeroBr(l.valor),
          })),
        },
      }),
    onSuccess: onSalvo,
  });

  const alterar = (i: number, campo: 'fase' | 'minimo' | 'valor', v: string) =>
    setLinhas((atual) => atual.map((l, j) => (i === j ? { ...l, [campo]: v } : l)));

  return (
    <div className="space-y-4">
      <Tabela
        cabecalho={
          <tr>
            <th className="py-2 pr-3">Fase</th>
            <th className="py-2 pr-3">A partir de (%)</th>
            <th className="py-2 pr-3">Até (%)</th>
            <th className="py-2 pr-3">Premiação (R$)</th>
            {editavel && <th className="py-2" />}
          </tr>
        }
      >
        {linhas.map((l, i) => {
          const proxima = linhas[i + 1];
          return (
            <tr key={i}>
              <td className="py-1.5 pr-3">
                <input
                  value={l.fase}
                  disabled={!editavel}
                  onChange={(e) => alterar(i, 'fase', e.target.value)}
                  className="w-28 rounded border border-slate-300 px-2 py-1 text-sm disabled:border-transparent disabled:bg-transparent"
                />
              </td>
              <td className="py-1.5 pr-3">
                <input
                  value={l.minimo}
                  disabled={!editavel}
                  onChange={(e) => alterar(i, 'minimo', e.target.value)}
                  className="num w-24 rounded border border-slate-300 px-2 py-1 text-sm disabled:border-transparent disabled:bg-transparent"
                />
              </td>
              <td className="num py-1.5 pr-3 text-slate-500">
                {proxima === undefined ? 'em diante' : `< ${proxima.minimo}`}
              </td>
              <td className="py-1.5 pr-3">
                <input
                  value={l.valor}
                  disabled={!editavel}
                  onChange={(e) => alterar(i, 'valor', e.target.value)}
                  className="num w-28 rounded border border-slate-300 px-2 py-1 text-sm disabled:border-transparent disabled:bg-transparent"
                />
              </td>
              {editavel && (
                <td className="py-1.5">
                  <button
                    onClick={() => setLinhas((a) => a.filter((_, j) => j !== i))}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-700"
                    title="Remover faixa"
                  >
                    <X className="size-4" />
                  </button>
                </td>
              )}
            </tr>
          );
        })}
      </Tabela>

      {salvar.isError && (
        <Aviso tom="erro">
          <p>{salvar.error.message}</p>
        </Aviso>
      )}

      {editavel && (
        <div className="flex flex-wrap gap-3">
          <Botao onClick={() => salvar.mutate()} carregando={salvar.isPending}>
            Salvar faixas
          </Botao>
          <Botao
            tipo="secundario"
            onClick={() =>
              setLinhas((a) => [
                ...a,
                { fase: `${a.length + 1}ª fase`, minimo: '', valor: '' },
              ])
            }
          >
            <Plus className="size-4" />
            Nova faixa
          </Botao>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------- editor balizador */

function EditorBalizador({
  versaoId,
  faixas,
  editavel,
  onSalvo,
}: {
  versaoId: string;
  faixas: FaixaBalizador[];
  editavel: boolean;
  onSalvo: () => void;
}) {
  const cargos = [...new Set(faixas.map((f) => f.cargo_codigo))];
  const [cargo, setCargo] = useState(cargos[0] ?? 'atendente');
  const doCargo = faixas.filter((f) => f.cargo_codigo === cargo);

  const [linhas, setLinhas] = useState(
    doCargo.map((f) => ({
      rotulo: f.rotulo,
      teto: paraPercentual(f.limite_superior),
      fator: paraPercentual(f.fator),
    })),
  );

  useEffect(() => {
    setLinhas(
      faixas
        .filter((f) => f.cargo_codigo === cargo)
        .map((f) => ({
          rotulo: f.rotulo,
          teto: paraPercentual(f.limite_superior),
          fator: paraPercentual(f.fator),
        })),
    );
  }, [faixas, cargo]);

  const salvar = useMutation({
    mutationFn: () =>
      api<{ ok: true }>(`/regras/versoes/${versaoId}/balizador`, {
        metodo: 'PUT',
        corpo: {
          cargoCodigo: cargo,
          faixas: linhas.map((l) => ({
            rotulo: l.rotulo,
            limiteSuperior: l.teto.trim() === '' ? null : paraFracao(l.teto),
            fator: paraFracao(l.fator),
          })),
        },
      }),
    onSuccess: onSalvo,
  });

  const alterar = (i: number, campo: 'rotulo' | 'teto' | 'fator', v: string) =>
    setLinhas((atual) => atual.map((l, j) => (i === j ? { ...l, [campo]: v } : l)));

  if (cargos.length === 0) {
    return (
      <Aviso tom="atencao">
        <p>Nenhuma faixa de balizador cadastrada nesta versão.</p>
      </Aviso>
    );
  }

  return (
    <div className="space-y-4">
      {cargos.length > 1 && (
        <div className="w-64">
          <Selecao rotulo="Cargo" valor={cargo} onChange={setCargo} opcoes={cargos} vazio="" />
        </div>
      )}

      <Tabela
        cabecalho={
          <tr>
            <th className="py-2 pr-3">Classificação</th>
            <th className="py-2 pr-3">Até (% da média)</th>
            <th className="py-2 pr-3">Fator aplicado (%)</th>
          </tr>
        }
      >
        {linhas.map((l, i) => (
          <tr key={i}>
            <td className="py-1.5 pr-3">
              <input
                value={l.rotulo}
                disabled={!editavel}
                onChange={(e) => alterar(i, 'rotulo', e.target.value)}
                className="w-52 rounded border border-slate-300 px-2 py-1 text-sm disabled:border-transparent disabled:bg-transparent"
              />
            </td>
            <td className="py-1.5 pr-3">
              <input
                value={l.teto}
                placeholder="sem teto"
                disabled={!editavel}
                onChange={(e) => alterar(i, 'teto', e.target.value)}
                className="num w-28 rounded border border-slate-300 px-2 py-1 text-sm disabled:border-transparent disabled:bg-transparent"
              />
            </td>
            <td className="py-1.5 pr-3">
              <input
                value={l.fator}
                disabled={!editavel}
                onChange={(e) => alterar(i, 'fator', e.target.value)}
                className="num w-28 rounded border border-slate-300 px-2 py-1 text-sm disabled:border-transparent disabled:bg-transparent"
              />
            </td>
          </tr>
        ))}
      </Tabela>

      <p className="text-xs text-slate-500">
        Deixe o teto vazio na última faixa. Fator 100% significa nenhum ajuste; abaixo disso reduz,
        acima aumenta.
      </p>

      {salvar.isError && (
        <Aviso tom="erro">
          <p>{salvar.error.message}</p>
        </Aviso>
      )}

      {editavel && (
        <Botao onClick={() => salvar.mutate()} carregando={salvar.isPending}>
          Salvar balizador
        </Botao>
      )}
    </div>
  );
}

/* --------------------------------------------------- editor parâmetros */

interface Piso {
  minimoAgendamentos: number;
  reducaoPercentual: number;
}

function EditorParametros({
  versaoId,
  parametros,
  cargos,
  editavel,
  onSalvo,
}: {
  versaoId: string;
  parametros: Parametro[];
  cargos: string[];
  editavel: boolean;
  onSalvo: () => void;
}) {
  const mapa = useMemo(
    () => new Map(parametros.map((p) => [p.chave, p.valor])),
    [parametros],
  );

  const [estado, setEstado] = useState(() => ({
    pisoComparecimento: (mapa.get('piso_comparecimento') as Piso | undefined) ?? {
      minimoAgendamentos: 0,
      reducaoPercentual: 0,
    },
    pisoBalizador: (mapa.get('piso_balizador') as Piso | undefined) ?? {
      minimoAgendamentos: 0,
      reducaoPercentual: 0,
    },
    mediaComparecimento:
      (mapa.get('media_comparecimento_minimo_para_entrar') as { minimoAgendamentos: number } | undefined)
        ?.minimoAgendamentos ?? 0,
    mediaBalizador:
      (mapa.get('media_balizador_minimo_para_entrar') as { minimoAgendamentos: number } | undefined)
        ?.minimoAgendamentos ?? 0,
    arredondamento: (mapa.get('arredondamento_taxa_casas_percentuais') as number | null) ?? 2,
    aplicaBalizador: (mapa.get('aplica_balizador_para_cargos') as string[] | undefined) ?? [],
    ajustes: mapa.get('ajustes') as RegrasDeAjuste | undefined,
    visao: mapa.get('visao_do_relatorio') as VisaoPorPilar | undefined,
    projecao: mapa.get('projecao_do_balizador') as RegraDeProjecao | undefined,
  }));

  const salvar = useMutation({
    mutationFn: () =>
      api<{ ok: true }>(`/regras/versoes/${versaoId}/parametros`, {
        metodo: 'PUT',
        corpo: {
          parametros: [
            { chave: 'piso_comparecimento', valor: estado.pisoComparecimento },
            { chave: 'piso_balizador', valor: estado.pisoBalizador },
            {
              chave: 'media_comparecimento_minimo_para_entrar',
              valor: { minimoAgendamentos: estado.mediaComparecimento },
            },
            {
              chave: 'media_balizador_minimo_para_entrar',
              valor: { minimoAgendamentos: estado.mediaBalizador },
            },
            { chave: 'arredondamento_taxa_casas_percentuais', valor: estado.arredondamento },
            { chave: 'aplica_balizador_para_cargos', valor: estado.aplicaBalizador },
            { chave: 'ajustes', valor: estado.ajustes },
            { chave: 'visao_do_relatorio', valor: estado.visao },
            { chave: 'projecao_do_balizador', valor: estado.projecao },
          ],
        },
      }),
    onSuccess: onSalvo,
  });

  const numero = (v: string): number => {
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <BlocoPiso
          titulo="Piso do comparecimento"
          explicacao="Volume mínimo para a taxa valer. Abaixo dele, a premiação de comparecimento é reduzida no percentual indicado — 100% zera."
          piso={estado.pisoComparecimento}
          editavel={editavel}
          onChange={(p) => setEstado((e) => ({ ...e, pisoComparecimento: p }))}
        />
        <BlocoPiso
          titulo="Piso do balizador"
          explicacao="Volume mínimo para entrar no balizador. Abaixo dele, a redução incide sobre a premiação TOTAL, não só sobre o comparecimento."
          piso={estado.pisoBalizador}
          editavel={editavel}
          onChange={(p) => setEstado((e) => ({ ...e, pisoBalizador: p }))}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Campo
          rotulo="Mínimo para entrar na média (comparecimento)"
          valor={String(estado.mediaComparecimento)}
          onChange={(v) => setEstado((e) => ({ ...e, mediaComparecimento: numero(v) }))}
          dica="Quem fica abaixo não entra na média que serve de base para Supervisor e Backoffice."
          desabilitado={!editavel}
        />
        <Campo
          rotulo="Mínimo para entrar na média (balizador)"
          valor={String(estado.mediaBalizador)}
          onChange={(v) => setEstado((e) => ({ ...e, mediaBalizador: numero(v) }))}
          dica="Na planilha original este número era diferente do piso individual."
          desabilitado={!editavel}
        />
        <Campo
          rotulo="Casas decimais do percentual"
          valor={String(estado.arredondamento)}
          onChange={(v) => setEstado((e) => ({ ...e, arredondamento: numero(v) }))}
          dica="A taxa é arredondada aqui antes de classificar a faixa, para o comprovante mostrar exatamente o que foi classificado."
          desabilitado={!editavel}
        />
      </div>

      <div>
        <p className="mb-2 text-xs font-medium tracking-wide text-slate-600 uppercase">
          Cargos sujeitos ao balizador
        </p>
        <div className="flex flex-wrap gap-4">
          {cargos.map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                disabled={!editavel}
                checked={estado.aplicaBalizador.includes(c)}
                onChange={(e) =>
                  setEstado((atual) => ({
                    ...atual,
                    aplicaBalizador: e.target.checked
                      ? [...atual.aplicaBalizador, c]
                      : atual.aplicaBalizador.filter((x) => x !== c),
                  }))
                }
              />
              {CARGOS[c] ?? c}
            </label>
          ))}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Cargo fora desta lista recebe a soma cheia, sem ajuste de produção.
        </p>
      </div>

      {estado.projecao !== undefined && (
        <BlocoProjecao
          projecao={estado.projecao}
          editavel={editavel}
          onChange={(v) => setEstado((e) => ({ ...e, projecao: v }))}
        />
      )}

      {estado.visao !== undefined && (
        <BlocoVisao
          visao={estado.visao}
          editavel={editavel}
          onChange={(v) => setEstado((e) => ({ ...e, visao: v }))}
        />
      )}

      {estado.ajustes !== undefined && (
        <BlocoAjustes
          ajustes={estado.ajustes}
          editavel={editavel}
          onChange={(a) => setEstado((e) => ({ ...e, ajustes: a }))}
        />
      )}

      {salvar.isError && (
        <Aviso tom="erro">
          <p>{salvar.error.message}</p>
        </Aviso>
      )}

      {editavel && (
        <Botao onClick={() => salvar.mutate()} carregando={salvar.isPending}>
          Salvar parâmetros
        </Botao>
      )}
    </div>
  );
}

/**
 * Corrige nome e vigência de uma versão que ainda não pagou ninguém.
 *
 * A rota existia desde o começo e nenhuma tela a chamava — então quem ativasse
 * com a data errada não tinha conserto pela interface, só apagar e refazer. Foi
 * assim que uma instalação ficou sem nenhuma versão vigente.
 *
 * Só aparece enquanto a versão tem zero fechamentos. Depois disso, mover a
 * data mudaria a explicação de um valor já pago, e o caminho é duplicar.
 */
function EditarCabecalho({
  versao,
  aoSalvar,
  aoCancelar,
}: {
  versao: {
    id: string;
    nome: string;
    vigencia_inicio: string;
    vigencia_fim: string | null;
    observacao: string | null;
  };
  aoSalvar: () => void;
  aoCancelar: () => void;
}) {
  const [nome, setNome] = useState(versao.nome);
  const [vigencia, setVigencia] = useState(versao.vigencia_inicio.slice(0, 10));
  const [fim, setFim] = useState(versao.vigencia_fim?.slice(0, 10) ?? '');
  const [observacao, setObservacao] = useState(versao.observacao ?? '');

  const salvar = useMutation({
    mutationFn: () =>
      api<{ ok: true; fechadaPelaPosterior: string | null }>(`/regras/versoes/${versao.id}`, {
        metodo: 'PUT',
        corpo: { nome, vigenciaInicio: vigencia, vigenciaFim: fim, observacao },
      }),
    onSuccess: aoSalvar,
  });

  return (
    <div className="space-y-4 rounded border border-slate-300 bg-slate-50 p-4">
      <p className="text-sm font-medium text-slate-900">Editar vigência</p>

      <Campo rotulo="Nome" valor={nome} onChange={setNome} />

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          rotulo="Vale a partir de"
          tipo="date"
          valor={vigencia}
          onChange={setVigencia}
          dica={`Competência de ${competencia(vigencia)}.`}
        />
        <Campo
          rotulo="Vale até (opcional)"
          tipo="date"
          valor={fim}
          onChange={setFim}
          dica={
            fim === ''
              ? 'Em branco: vale em diante, até outra versão assumir. Ativar uma versão posterior encerra esta sozinha, no dia anterior ao início dela.'
              : `Último dia em que esta versão vale — competência de ${competencia(fim)}. Depois dessa data o fechamento procura outra; se não houver, recusa.`
          }
        />
      </div>

      <Campo rotulo="Observação" valor={observacao} onChange={setObservacao} />

      {salvar.isError && (
        <Aviso tom="erro">
          <p>{salvar.error.message}</p>
        </Aviso>
      )}

      <div className="flex gap-3">
        <Botao
          onClick={() => salvar.mutate()}
          carregando={salvar.isPending}
          desabilitado={nome.trim() === '' || vigencia === ''}
        >
          Salvar
        </Botao>
        <Botao tipo="secundario" onClick={aoCancelar}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}

/**
 * Projeção do balizador pelos dias de presença.
 *
 * O texto aqui é longo de propósito. É a regra mais fácil de ligar sem
 * entender, e a que mais muda valores: quem liga precisa saber que o número
 * comparado deixa de ser o que está na base.
 */
function BlocoProjecao({
  projecao,
  editavel,
  onChange,
}: {
  projecao: RegraDeProjecao;
  editavel: boolean;
  onChange: (v: RegraDeProjecao) => void;
}) {
  const minimo = Number(projecao.minimoDeDiasDePresenca);

  return (
    <div className="space-y-4 rounded border border-slate-200 p-4">
      <div>
        <p className="font-medium text-slate-900">Balizador: projeção por dias de presença</p>
        <p className="mt-1 max-w-prose text-xs text-slate-500">
          O balizador compara o volume da pessoa com a média da equipe. Quem passou metade do mês
          fora tem metade do volume e cai como "abaixo da média" sem ter produzido menos por dia.
        </p>
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          disabled={!editavel}
          className="mt-1"
          checked={projecao.ativa}
          onChange={(e) => onChange({ ...projecao, ativa: e.target.checked })}
        />
        <span>
          Projetar o volume para o mês cheio antes de comparar
          <span className="mt-1 block text-xs text-slate-500">
            <span className="font-mono">
              (agendamentos ÷ dias em que a pessoa aparece) × dias em que a operação teve
              agendamento
            </span>
            . Os dois números saem do próprio dado, sem digitação. Para quem esteve presente o mês
            todo a conta é identidade — nada muda. O valor projetado entra na comparação{' '}
            <strong>e no piso</strong>.
          </span>
        </span>
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          rotulo="Mínimo de dias de presença"
          valor={String(projecao.minimoDeDiasDePresenca)}
          onChange={(v) => {
            const n = Math.trunc(Number(v.replace(/[^\d]/g, '')));
            onChange({ ...projecao, minimoDeDiasDePresenca: Number.isFinite(n) ? n : 1 });
          }}
          desabilitado={!editavel || !projecao.ativa}
          dica="Com menos dias que isto a projeção não se aplica: fica o volume bruto e a pessoa é apontada na prévia."
        />
      </div>

      {projecao.ativa && minimo <= 1 && (
        <Aviso tom="erro" titulo="Com 1, qualquer presença é projetada">
          <p>
            Uma pessoa que apareceu em <strong>um único dia</strong> terá esse dia multiplicado
            pelo mês inteiro. A projeção multiplica a variação junto com o volume: um dia cheio
            coloca a pessoa acima de toda a equipe, um dia fraco a coloca abaixo — a mesma pessoa,
            no mesmo mês, pelo dia que calhou de acontecer.
          </p>
          <p className="mt-2">
            Só use 1 se você aceita esse resultado. Um mínimo de 4 ou 5 dias já impede o caso
            injusto, porque a média de alguns dias absorve o dia fora da curva.
          </p>
        </Aviso>
      )}

      <p className="max-w-prose text-xs text-slate-500">
        A projeção não distingue o motivo da ausência: férias e falta sem justificativa são
        normalizadas igual. A falta já é descontada pela regra de 1/3 em "Faltas e dias
        trabalhados"; descontá-la de novo aqui cobraria duas vezes pelo mesmo fato.
      </p>
    </div>
  );
}

const VISOES: { valor: VisaoDoRelatorio; rotulo: string; dica: string }[] = [
  {
    valor: 'data_do_agendamento',
    rotulo: 'Data do agendamento',
    dica: 'O dia em que o paciente foi, ou iria ser, atendido.',
  },
  {
    valor: 'data_de_criacao',
    rotulo: 'Data de criação',
    dica: 'O dia em que o atendente lançou o agendamento no sistema.',
  },
];

const PILARES: { chave: keyof VisaoPorPilar; rotulo: string; dica: string }[] = [
  {
    chave: 'comparecimento',
    rotulo: 'Comparecimento',
    dica: 'O desempenho individual: quanto do que estava agendado compareceu.',
  },
  {
    chave: 'balizador',
    rotulo: 'Balizador',
    dica: 'O volume de trabalho comparado com a média da equipe.',
  },
];

/**
 * Qual data recorta o relatório em cada pilar.
 *
 * É o mesmo relatório de agendamentos nas duas opções — muda só o recorte de
 * período. Um agendamento marcado em 28/08 para 10/09 cai em agosto por uma
 * data e em setembro pela outra, então trocar isto muda os números do mês.
 */
function BlocoVisao({
  visao,
  editavel,
  onChange,
}: {
  visao: VisaoPorPilar;
  editavel: boolean;
  onChange: (v: VisaoPorPilar) => void;
}) {
  const diferentes = visao.comparecimento !== visao.balizador;

  return (
    <div className="space-y-4 rounded border border-slate-200 p-4">
      <div>
        <p className="font-medium text-slate-900">Qual data recorta o relatório</p>
        <p className="mt-1 max-w-prose text-xs text-slate-500">
          Os dois pilares leem o mesmo relatório de agendamentos. O que muda aqui é qual data
          define se um agendamento pertence à competência.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {PILARES.map((pilar) => (
          <div key={pilar.chave}>
            <p className="text-xs font-medium tracking-wide text-slate-600 uppercase">
              {pilar.rotulo}
            </p>
            <p className="mt-0.5 mb-2 text-xs text-slate-500">{pilar.dica}</p>
            <div className="space-y-1.5">
              {VISOES.map((v) => {
                const ativo = visao[pilar.chave] === v.valor;
                return (
                  <button
                    key={v.valor}
                    disabled={!editavel}
                    onClick={() => onChange({ ...visao, [pilar.chave]: v.valor })}
                    className={clsx(
                      'w-full rounded border px-3 py-2 text-left transition disabled:cursor-not-allowed',
                      ativo
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-200 text-slate-600 hover:border-slate-400 disabled:hover:border-slate-200',
                    )}
                  >
                    <span className="block text-sm font-medium">{v.rotulo}</span>
                    <span
                      className={clsx('block text-xs', ativo ? 'text-slate-300' : 'text-slate-500')}
                    >
                      {v.dica}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {diferentes && (
        <Aviso titulo="Os dois pilares vão ler recortes diferentes">
          <p>
            O fechamento vai buscar o relatório duas vezes, uma por data, e cada pilar usa o seu.
            É mais lento e é esperado: são de fato dois recortes do mesmo relatório.
          </p>
        </Aviso>
      )}

      <p className="max-w-prose text-xs text-slate-500">
        Mudar isto muda o valor apurado. Como toda regra, vale a partir da vigência desta versão —
        os meses já fechados continuam com a visão que usaram.
      </p>
    </div>
  );
}

const ROTULO_DO_AJUSTE: Record<string, string> = {
  proporcionalidade: 'Proporcionalidade por dias trabalhados',
  falta_injustificada: 'Falta não justificada',
  falta_justificada: 'Falta justificada',
};

/**
 * Faltas e proporcionalidade.
 *
 * Inclui a ORDEM em que os três se aplicam, porque ela muda o valor: tirar um
 * terço de R$ 787,50 e depois descontar R$ 80 dá R$ 445,00; descontar os R$ 80
 * antes dá R$ 471,67.
 */
function BlocoAjustes({
  ajustes,
  editavel,
  onChange,
}: {
  ajustes: RegrasDeAjuste;
  editavel: boolean;
  onChange: (a: RegrasDeAjuste) => void;
}) {
  const num = (v: string): number => {
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  const mover = (de: number, para: number) => {
    if (para < 0 || para >= ajustes.ordem.length) return;
    const ordem = [...ajustes.ordem];
    const [item] = ordem.splice(de, 1);
    if (item === undefined) return;
    ordem.splice(para, 0, item);
    onChange({ ...ajustes, ordem });
  };

  return (
    <div className="space-y-5 rounded border border-slate-200 p-4">
      <div>
        <p className="font-medium text-slate-900">Faltas e dias trabalhados</p>
        <p className="mt-1 text-xs text-slate-500">
          Ajustes aplicados depois do balizador, sobre a premiação já apurada.
        </p>
      </div>

      <div>
        <p className="mb-2 text-xs font-medium tracking-wide text-slate-600 uppercase">
          Ordem de aplicação
        </p>
        <ol className="space-y-1">
          {ajustes.ordem.map((tipo, i) => (
            <li key={tipo} className="flex items-center gap-2 text-sm">
              <span className="w-5 text-slate-400">{i + 1}.</span>
              <span className="flex-1">{ROTULO_DO_AJUSTE[tipo] ?? tipo}</span>
              {editavel && (
                <>
                  <button
                    onClick={() => mover(i, i - 1)}
                    disabled={i === 0}
                    className="rounded px-2 py-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                    title="Subir"
                  >
                    &uarr;
                  </button>
                  <button
                    onClick={() => mover(i, i + 1)}
                    disabled={i === ajustes.ordem.length - 1}
                    className="rounded px-2 py-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                    title="Descer"
                  >
                    &darr;
                  </button>
                </>
              )}
            </li>
          ))}
        </ol>
        <p className="mt-1 text-xs text-slate-500">
          A ordem muda o valor final: um desconto em reais aplicado antes ou depois de um fator
          percentual dá resultados diferentes.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium tracking-wide text-slate-600 uppercase">
            Base de dias do mês
          </span>
          <select
            value={ajustes.proporcionalidade.base}
            disabled={!editavel}
            onChange={(e) =>
              onChange({
                ...ajustes,
                proporcionalidade: {
                  ...ajustes.proporcionalidade,
                  base: e.target.value as 'dias_do_mes' | 'fixo',
                },
              })
            }
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-50"
          >
            <option value="dias_do_mes">Dias reais do mês (28, 29, 30 ou 31)</option>
            <option value="fixo">Número fixo</option>
          </select>
        </label>

        <Campo
          rotulo="Dias fixos"
          valor={String(ajustes.proporcionalidade.diasFixos)}
          onChange={(v) =>
            onChange({
              ...ajustes,
              proporcionalidade: { ...ajustes.proporcionalidade, diasFixos: num(v) },
            })
          }
          desabilitado={!editavel || ajustes.proporcionalidade.base !== 'fixo'}
          dica={
            ajustes.proporcionalidade.base === 'fixo'
              ? 'Todo mês divide por este número, mesmo os de 31 dias.'
              : 'Sem efeito: a base acima está usando os dias reais do mês.'
          }
        />

        <Campo
          rotulo="Divisor do salário"
          valor={String(ajustes.faltaJustificada.divisorDoSalario)}
          onChange={(v) =>
            onChange({
              ...ajustes,
              faltaJustificada: { ...ajustes.faltaJustificada, divisorDoSalario: num(v) },
            })
          }
          dica="Outra conta: aqui sai o valor do dia de salário descontado por falta justificada."
          desabilitado={!editavel}
        />

        <Campo
          rotulo="Dias de salário por falta justificada"
          valor={String(ajustes.faltaJustificada.diasPorFalta)}
          onChange={(v) =>
            onChange({
              ...ajustes,
              faltaJustificada: { ...ajustes.faltaJustificada, diasPorFalta: num(v) },
            })
          }
          dica="Quanto cada falta justificada desconta, em dias de salário."
          desabilitado={!editavel}
        />

        <Campo
          rotulo="Divisor da falta não justificada"
          valor={String(ajustes.faltaInjustificada.divisorPorFalta)}
          onChange={(v) =>
            onChange({
              ...ajustes,
              faltaInjustificada: { ...ajustes.faltaInjustificada, divisorPorFalta: num(v) },
            })
          }
          dica="Cada falta tira 1 dividido por este número. Com 3, cada falta tira um terço."
          desabilitado={!editavel}
        />

        <Campo
          rotulo="Faltas não justificadas que zeram"
          valor={String(ajustes.faltaInjustificada.faltasParaZerar)}
          onChange={(v) =>
            onChange({
              ...ajustes,
              faltaInjustificada: { ...ajustes.faltaInjustificada, faltasParaZerar: num(v) },
            })
          }
          dica="A partir desta quantidade, a premiação é zerada."
          desabilitado={!editavel}
        />
      </div>

      <div className="space-y-2">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!editavel}
            className="mt-1"
            checked={ajustes.proporcionalidade.descontarFaltasInjustificadasPorPadrao}
            onChange={(e) =>
              onChange({
                ...ajustes,
                proporcionalidade: {
                  ...ajustes.proporcionalidade,
                  descontarFaltasInjustificadasPorPadrao: e.target.checked,
                },
              })
            }
          />
          <span>
            A falta não justificada também tira o dia trabalhado
            <span className="block text-xs text-slate-500">
              Ligado, ela pesa duas vezes: perde a fração da premiação e ainda reduz a
              proporcionalidade. Cada lançamento pode divergir deste padrão.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!editavel}
            className="mt-1"
            checked={ajustes.proporcionalidade.limitarEmCemPorCento}
            onChange={(e) =>
              onChange({
                ...ajustes,
                proporcionalidade: {
                  ...ajustes.proporcionalidade,
                  limitarEmCemPorCento: e.target.checked,
                },
              })
            }
          />
          <span>
            Limitar a proporção em 100%
            <span className="block text-xs text-slate-500">
              Impede que informar mais dias que o mês tem pague acima do valor integral.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!editavel}
            className="mt-1"
            checked={ajustes.pisoEmZero}
            onChange={(e) => onChange({ ...ajustes, pisoEmZero: e.target.checked })}
          />
          <span>
            Não deixar a premiação ficar negativa
            <span className="block text-xs text-slate-500">
              Se o desconto passar do valor, para em zero: não paga, mas também não vira dívida.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}

function BlocoPiso({
  titulo,
  explicacao,
  piso,
  editavel,
  onChange,
}: {
  titulo: string;
  explicacao: string;
  piso: Piso;
  editavel: boolean;
  onChange: (p: Piso) => void;
}) {
  const restante = 100 - piso.reducaoPercentual;

  return (
    <div className="rounded border border-slate-200 p-4">
      <p className="font-medium text-slate-900">{titulo}</p>
      <p className="mt-1 text-xs text-slate-500">{explicacao}</p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium tracking-wide text-slate-600 uppercase">
            Mínimo de agendamentos
          </span>
          <input
            value={String(piso.minimoAgendamentos)}
            disabled={!editavel}
            onChange={(e) =>
              onChange({ ...piso, minimoAgendamentos: Number(e.target.value.replace(',', '.')) || 0 })
            }
            className="num w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium tracking-wide text-slate-600 uppercase">
            Redução (%)
          </span>
          <input
            value={String(piso.reducaoPercentual)}
            disabled={!editavel}
            onChange={(e) =>
              onChange({ ...piso, reducaoPercentual: Number(e.target.value.replace(',', '.')) || 0 })
            }
            className="num w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
          />
        </label>
      </div>

      <p className="mt-3 flex items-start gap-2 rounded bg-slate-50 p-2 text-xs text-slate-600">
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-slate-400" />
        {piso.reducaoPercentual >= 100
          ? `Quem ficar abaixo de ${piso.minimoAgendamentos} agendamentos perde tudo (${reais(0)}).`
          : `Quem ficar abaixo de ${piso.minimoAgendamentos} agendamentos recebe ${restante}% do valor apurado.`}
      </p>
    </div>
  );
}

/* ------------------------------------------------------ editor filtros */

const PAPEIS: Record<string, string> = {
  agendado_e_atendido: 'conta nos dois',
  agendado: 'só no denominador',
  ignorar: 'ignorado',
  exige: 'exigido',
};

function EditorFiltros({
  versaoId,
  filtros,
  editavel,
  onSalvo,
}: {
  versaoId: string;
  filtros: Filtro[];
  editavel: boolean;
  onSalvo: () => void;
}) {
  const [base, setBase] = useState('comparecimento');

  const alterar = useMutation({
    mutationFn: (v: { id: string; papel: string }) =>
      api<{ ok: true }>(`/regras/versoes/${versaoId}/filtro/${v.id}`, {
        metodo: 'PUT',
        corpo: { papel: v.papel },
      }),
    onSuccess: onSalvo,
  });

  // Só os filtros por id: são os que valem quando o dado vem do banco. Os por
  // nome existem para bases históricas importadas de planilha.
  const daBase = filtros.filter((f) => f.base === base && f.tipo === 'status_id');

  return (
    <div className="space-y-4">
      <div className="w-64">
        <Selecao
          rotulo="Base"
          valor={base}
          onChange={setBase}
          opcoes={['comparecimento', 'balizador']}
          vazio=""
        />
      </div>

      {alterar.isError && (
        <Aviso tom="erro">
          <p>{alterar.error.message}</p>
        </Aviso>
      )}

      <Tabela
        cabecalho={
          <tr>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3 text-right">id</th>
            <th className="py-2 pr-3">Como conta</th>
          </tr>
        }
      >
        {daBase.map((f) => (
          <tr key={f.id}>
            <td className="py-1.5 pr-3">{f.rotulo ?? f.valor}</td>
            <td className="num py-1.5 pr-3 text-slate-400">{f.valor}</td>
            <td className="py-1.5 pr-3">
              <select
                value={f.papel}
                disabled={!editavel || alterar.isPending}
                onChange={(e) => alterar.mutate({ id: f.id, papel: e.target.value })}
                className="rounded border border-slate-300 bg-white px-2 py-1 text-sm
                           disabled:border-transparent disabled:bg-transparent"
              >
                {Object.entries(PAPEIS)
                  .filter(([p]) => p !== 'exige')
                  .map(([p, rotulo]) => (
                    <option key={p} value={p}>
                      {rotulo}
                    </option>
                  ))}
              </select>
            </td>
          </tr>
        ))}
      </Tabela>

      <p className="text-xs text-slate-500">
        O status é identificado pelo <strong>id</strong>, não pelo nome — a réplica grava
        "Desmarcado pela clinica" sem acento, e casar por texto já custou 15 agendamentos numa
        conferência.
      </p>
    </div>
  );
}
